import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { AddOn_EntityFilter, AddOn_Label, AddOn_SearchTerms } from '@nu-art/search';
import { ModuleFE_Thunderstorm, resolveEntity, resolveId } from '@nu-art/thunderstorm-frontend';
import { __stringify, formatTimestamp, generateHex, md5, MimeType_json } from '@nu-art/ts-common';
import { WorkHubItem } from '@nu-art/work-hub-frontend/core';
import { QuaiBaseAddOnKeys } from './base.js';
import { Dialog_Confirmation } from '../frontend/index.js';
import { Renderer_SearchResult, SearchResult_BaseRightClickActions } from '../frontend/ui/components/Renderer_SearchResult/Renderer_SearchResult.js';
import { displayEntityDependencies } from '../frontend/ui/consts.js';
export function createSearchIntegration(config) {
    const resolveItem = resolveEntity(config.module);
    const entityLabel = config.entityLabel ?? config.tag[1];
    const labelField = config.labelField ?? 'label';
    const resolveLabel = (entity) => {
        if (!entity)
            return undefined;
        if (config.customLabelResolver)
            return config.customLabelResolver(entity);
        return entity[labelField];
    };
    const UtilObject = {
        generateTabId: (id) => md5(`${config.dbDef.dbKey}-${id}`),
        renderLabel: (item) => {
            const entity = resolveItem(item);
            return resolveLabel(entity);
        },
        openEditor: (item, overrides) => {
            if (config.customOpenEditor && !overrides) {
                config.customOpenEditor(item);
                return;
            }
            const id = resolveId(item) ?? `###NEWITEM###-${generateHex(8)}`;
            workHubItem.openTab(UtilObject.generateTabId(id), UtilObject.renderLabel(item) ?? `New ${entityLabel}`, { itemId: id, editMode: id.startsWith('###NEWITEM###'), ...overrides });
        },
        copyId: (item) => {
            const id = resolveId(item);
            if (id)
                ModuleFE_Thunderstorm.copyToClipboard(id, `${entityLabel} Id Copied`);
        },
        copyObj: (item) => {
            const obj = resolveItem(item);
            if (obj) {
                const string = __stringify(obj, true);
                ModuleFE_Thunderstorm.copyToClipboard(string, `${entityLabel} Copied`);
            }
        },
        exportJSON: (item) => {
            const obj = resolveItem(item);
            if (!obj)
                return;
            const string = __stringify(obj, true);
            const resolvedName = resolveLabel(obj) ?? entityLabel;
            const title = `${entityLabel} - ${resolvedName} - [${formatTimestamp('DD/MM/YYYY HH:mm')}]`;
            ModuleFE_Thunderstorm.downloadFile({
                mimeType: MimeType_json,
                charset: 'UTF-8',
                fileName: title,
                content: string,
            });
        },
    };
    const Manager = config.ManagerComponent;
    const defaultMenuResolver = async (tab) => {
        const entity = resolveItem(tab.renderArgs.itemId);
        if (!entity)
            return [];
        const deleteAction = config.customDeleteAction
            ? () => config.customDeleteAction(entity, () => workHubItem.closeTab(tab.id))
            : () => {
                const moduleWithDelete = config.module;
                Dialog_Confirmation.show({
                    header: 'Are you sure?',
                    content: () => _jsxs(_Fragment, { children: [_jsxs("div", { children: ["You're about to delete the ", entityLabel.toLowerCase()] }), _jsx("div", { children: resolveLabel(entity) })] }),
                    buttons: [
                        {
                            label: 'Yes, Delete',
                            isAsync: true,
                            action: async () => {
                                await moduleWithDelete.v1.delete(entity).executeSync();
                                workHubItem.closeTab(tab.id);
                            },
                        },
                        {
                            label: 'Don\'t Delete',
                            isPreferred: true,
                            action: () => {
                            },
                        },
                    ],
                });
            };
        const sections = [
            {
                actions: [{
                        label: `${tab.renderArgs.editMode ? 'View' : 'Edit'} Mode`,
                        action: () => workHubItem.updateArgs(tab.id, { editMode: !tab.renderArgs.editMode }),
                        visible: !tab.renderArgs.readonly,
                    }],
            },
            {
                actions: [
                    { label: 'Copy ID', action: () => UtilObject.copyId(tab.renderArgs.itemId) },
                    { label: 'Copy Item', action: () => UtilObject.copyObj(tab.renderArgs.itemId) },
                    { label: 'Export JSON', action: () => UtilObject.exportJSON(tab.renderArgs.itemId) },
                ],
            },
            ...(config.extraMenuSections?.(entity, tab) ?? []),
        ];
        const hasDelete = !!config.customDeleteAction || !!config.module.v1?.delete;
        if (hasDelete) {
            sections.push({
                actions: [{
                        label: `Delete ${entityLabel}`,
                        action: deleteAction,
                    }],
            });
        }
        return sections;
    };
    const workHubItem = new WorkHubItem(config.dbDef.dbKey)
        .setTag(config.tag[0])
        .setModulesToAwait(config.modulesToAwait)
        .setRenderer((whi, tabId, args) => _jsx(Manager, { workHubItem: whi, tabId: tabId, renderArgs: args }))
        .setCustomMenuActionsResolver(config.customMenuResolver
        ? (tab) => config.customMenuResolver(tab, workHubItem)
        : defaultMenuResolver);
    const compatibleAddOnKeys = [...QuaiBaseAddOnKeys, ...(config.extraAddOnKeys ?? [])];
    const searchItem = {
        module: config.module,
        entityLabel,
        compatibleAddOnKeys,
        labelResolver: (result) => UtilObject.renderLabel(result.id) ?? 'N/A',
        addOnMethods: {
            [AddOn_Label.methodName]: (pointer) => (UtilObject.renderLabel(pointer.id) ?? 'N/A').toLowerCase(),
            [AddOn_EntityFilter.methodName]: (result) => result.dbKey,
            [AddOn_SearchTerms.methodName]: (result) => {
                const item = resolveItem(result.id);
                if (!item)
                    return [];
                if (config.searchTermsExtractor)
                    return config.searchTermsExtractor(item);
                return [(resolveLabel(item) ?? '').toLowerCase(), item._id];
            },
            ...config.extraSearchAddOns,
        },
        resultRenderer: config.resultRenderer ?? ((result, style) => {
            const entity = resolveItem(result.id);
            const resolvedEntityLabel = config.resultEntityLabel ? config.resultEntityLabel(entity) : entityLabel;
            return _jsx(Renderer_SearchResult, { entity: entity, entityLabel: resolvedEntityLabel, style: style, itemLabel: UtilObject.renderLabel(entity) ?? 'N/A', subtitle: config.resultSubtitle?.(entity), onClickAction: () => UtilObject.openEditor(entity), rightClickActions: [
                    ...SearchResult_BaseRightClickActions(entity),
                    { label: 'Check Usage', action: () => displayEntityDependencies(result.dbKey, result.id) }
                ] });
        }),
    };
    return { WorkHubItem: workHubItem, UtilObject, SearchItem: searchItem };
}
//# sourceMappingURL=workhub-search-factory.js.map